import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import {
  AuthGuard,
  TicketTransferGuard,
  PopulationGuard,
  StepType,
  WizardType,
  MemberResolver,
  MemberCardResolver,
  PopulationResolver,
  BalanceResolver,
  TicketResolver,
  AddressBookResolver,
  RegistrationStepResolver,
  RegistrationMembershipOptionResolver,
  CardResolver,
  AddressResolver,
  SeatingDetailResolver,
  ProductCodeResolver,
  CountryResolver,
  MemberLoyaltyStatusResolver,
  MaintenanceGuard,
} from '@fgb/core';

// General
import { MasterPageComponent } from './pages/master/master-page/master-page.component';
import { FaqPageComponent } from './pages/faq-page/faq-page.component';
import { ContactUsPageComponent } from './pages/contact-us-page/contact-us-page.component';
import { TermsPageComponent } from './pages/terms-page/terms-page.component';
import { NotFoundPageComponent } from './pages/not-found-page/not-found-page.component';

// Account
import { AccountDetailsPageComponent } from './pages/account-details-page/account-details-page.component';
import { CardPageComponent } from './pages/card-page/card-page.component';

// Events & Tickets
import { TicketTransferPageComponent } from './pages/ticket-transfer-page/ticket-transfer-page.component';
import { TicketsPageComponent } from './pages/ticket-transfer-page/tickets-page/tickets-page.component';
import { TransferConfirmPageComponent } from './pages/ticket-transfer-page/transfer-confirm-page/transfer-confirm-page.component';
import { TicketTransferSuccessPageComponent } from './pages/ticket-transfer-page/ticket-transfer-success-page/ticket-transfer-success-page.component';
import { ContactsPageComponent } from './pages/contacts-page/contacts-page.component';

// Unauthenticated
import { LoginPageComponent } from './pages/login-page/login-page.component';
import { LoginAccountsPageComponent } from './pages/login-accounts-page/login-accounts-page.component';
import { SingleSignOnComponent } from './modules/user/components/single-sign-on/single-sign-on.component';
import { MaintenancePageComponent } from './pages/maintenance-page/maintenance-page.component';
// Registration
import { RegistrationLayoutPageComponent } from './modules/registration/pages/registration-layout-page/registration-layout-page.component';
import { RegistrationEntryPageComponent } from './modules/registration/pages/registration-entry-page/registration-entry-page.component';
import { RegistrationSuccessPageComponent } from './modules/registration/pages/registration-success-page/registration-success-page.component';
import { RegistrationFailurePageComponent } from './modules/registration/pages/registration-failure-page/registration-failure-page.component';
import { RegistrationPaymentCallbackPageComponent } from './modules/registration/pages/registration-payment-callback-page/registration-payment-callback-page.component';
import { RegistrationPersonalDetailsComponent } from './modules/registration/pages/steps/registration-personal-details/registration-personal-details.component';
import { RegistrationShippingAddressComponent } from './modules/registration/pages/steps/registration-shipping-address/registration-shipping-address.component';
import { RegistrationTermsAndConditionsComponent } from './modules/registration/pages/steps/registration-terms-and-conditions/registration-terms-and-conditions.component';
import { RegistrationProviderAuthenticationComponent } from './modules/registration/pages/steps/registration-provider-authentication/registration-provider-authentication.component';
import { RegistrationProviderTranslationComponent } from './modules/registration/pages/steps/registration-provider-translation/registration-provider-translation.component';
import { RegistrationAddChildrenComponent } from './modules/registration/pages/steps/registration-add-children/registration-add-children.component';
import { RegistrationMembershipOptionsComponent } from './modules/registration/pages/steps/registration-membership-options/registration-membership-options.component';
import { RegistrationPaymentComponent } from './modules/registration/pages/steps/registration-payment/registration-payment.component';
import { RegistrationOperatorNameComponent } from './modules/registration/pages/steps/registration-operator-name/registration-operator-name.component';
import { RegistrationChildQuestionsComponent } from './modules/registration/pages/steps/registration-child-questions/registration-child-questions.component';
import { RegistrationQuestionsComponent } from './modules/registration/pages/steps/registration-questions/registration-questions.component';
import { RegistrationPartnerInviteComponent } from './modules/registration/pages/steps/registration-partner-invite/registration-partner-invite.component';
import { RegistrationOptOutAlertsComponent } from './modules/registration/pages/steps/registration-opt-out-alerts/registration-opt-out-alerts.component';
import { RegistrationOptOutEmailsComponent } from './modules/registration/pages/steps/registration-opt-out-emails/registration-opt-out-emails.component';
import { RegistrationProviderOauthComponent } from './modules/registration/pages/steps/registration-provider-oauth/registration-provider-oauth.component';
// Population Pages
import { TestPopHomePageComponent } from './pages/test-pop-home-page/test-pop-home-page.component';

// Club Pages

import { RegistrationChooseMembershipOptionTypeComponent } from './modules/registration/pages/steps/registration-choose-membership-option-type/registration-choose-membership-option-type';

import { V1SingleSignOnComponent } from './modules/user/components/v1-single-sign-on/v1-single-sign-on.component';
import { RegistrationGetProviderDetailsComponent } from './modules/registration/pages/steps/registration-get-provider-details/registration-get-provider-details.component';

const routes: Routes = [
  {
    path: '',
    canActivate: [AuthGuard],
    canActivateChild: [MaintenanceGuard],
    component: MasterPageComponent,
    resolve: {
      member: MemberResolver,
      memberCard: MemberCardResolver,
      balances: BalanceResolver,
      populations: PopulationResolver,
      productCodes: ProductCodeResolver,
      cards: CardResolver,
    },
    children: [
      {
        path: '',
        canActivate: [PopulationGuard],
        data: {
          exclude: ['testPop'],
          popRedirect: 'test',
        },
        component: TicketTransferPageComponent,
      },
      {
        path: 'cards',
        resolve: { cards: CardResolver },
        component: CardPageComponent,
      },
      {
        path: 'test',
        canActivate: [PopulationGuard],
        data: {
          include: ['testPop'],
          popRedirect: '',
        },
        component: TestPopHomePageComponent,
      },
      {
        path: 'account',
        component: AccountDetailsPageComponent,
        resolve: {
          member: MemberResolver,
          balances: BalanceResolver,
          seating: SeatingDetailResolver,
          memberLoyaltyStatus: MemberLoyaltyStatusResolver,
        },
      },

      { path: 'faq', component: FaqPageComponent },
      { path: 'terms', component: TermsPageComponent },
      { path: 'contact', component: ContactUsPageComponent },
      {
        path: 'ticketTransfer',
        children: [
          {
            path: '',
            component: TicketTransferPageComponent,
          },
          {
            path: 'tickets/:gameNumber',
            component: TicketsPageComponent,
            resolve: {
              ticketResolver: TicketResolver,
            },
          },
          {
            path: 'confirm',
            component: TransferConfirmPageComponent,
            resolve: { addressBookResolver: AddressBookResolver },
            canActivate: [TicketTransferGuard],
          },
          { path: 'success', component: TicketTransferSuccessPageComponent },
        ],
      },
      {
        path: 'contacts',
        component: ContactsPageComponent,
        resolve: { addressBookResolver: AddressBookResolver },
      },

      { path: '404page', component: NotFoundPageComponent },
    ],
  },
  { path: '', redirectTo: 'login', pathMatch: 'full' },
  { path: 'login', component: LoginPageComponent, canActivate: [MaintenanceGuard] },
  { path: 'login/accounts', component: LoginAccountsPageComponent, canActivate: [MaintenanceGuard] },
  {
    path: 'register',
    canActivateChild: [MaintenanceGuard],
    resolve: { registrationStep: RegistrationStepResolver, country: CountryResolver },
    children: [
      {
        path: 'payment',
        children: [
          {
            path: '',
            component: RegistrationPaymentComponent,
            data: { registrationStep: StepType.Payment },
            resolve: { member: MemberResolver, address: AddressResolver },
          },
        ],
      },
    ],
  },
  {
    path: 'register',
    canActivateChild: [MaintenanceGuard],
    component: RegistrationLayoutPageComponent,
    resolve: { CountryResolver },
    children: [
      {
        path: 'success',
        component: RegistrationSuccessPageComponent,
      },
      {
        path: 'failure',
        component: RegistrationFailurePageComponent,
      },
      // Entry points
      {
        path: 'firsttime',
        component: RegistrationEntryPageComponent,
        canActivate: [AuthGuard],
        data: { wizardType: WizardType.FirstTimeRegistration },
      },
      {
        path: 'quick',
        component: RegistrationEntryPageComponent,
        data: { wizardType: WizardType.QuickRegistration },
      },
      {
        path: 'partner',
        component: RegistrationEntryPageComponent,
        data: { wizardType: WizardType.PartnerRegistration },
      },
      {
        path: 'new-member',
        component: RegistrationEntryPageComponent,
        data: { wizardType: WizardType.NewMemberRegistration },
      },
      {
        path: 'guardian',
        component: RegistrationEntryPageComponent,
        data: { wizardType: WizardType.GuardianRegistration },
      },
      {
        path: 'newchildren',
        component: RegistrationEntryPageComponent,
        data: { wizardType: WizardType.ChildRegistration },
      },
      {
        path: 'external',
        component: RegistrationEntryPageComponent,
        data: { wizardType: WizardType.External },
      },
      {
        path: 'in-park',
        component: RegistrationEntryPageComponent,
        data: { wizardType: WizardType.InPark },
      },
      // Steps
      {
        path: 'details',
        component: RegistrationPersonalDetailsComponent,
        resolve: { registrationStep: RegistrationStepResolver },
        data: { registrationStep: StepType.PersonalDetails },
      },
      {
        path: 'getproviderdetails',
        component: RegistrationGetProviderDetailsComponent,
        resolve: { registrationStep: RegistrationStepResolver },
        data: { registrationStep: StepType.GetProviderDetails },
      },
      {
        path: 'children',
        component: RegistrationAddChildrenComponent,
        resolve: { registrationStep: RegistrationStepResolver },
        data: { registrationStep: StepType.AddChild },
      },
      {
        path: 'memberships',
        component: RegistrationMembershipOptionsComponent,
        resolve: { membershipOptions: RegistrationMembershipOptionResolver, registrationStep: RegistrationStepResolver },
        data: { registrationStep: StepType.MembershipOption },
      },
      {
        path: 'choosersn',
        component: RegistrationChooseMembershipOptionTypeComponent,
        resolve: { membershipOptions: RegistrationMembershipOptionResolver, registrationStep: RegistrationStepResolver },
        data: { registrationStep: StepType.ChooseMembershipOption },
      },
      {
        path: 'shipping',
        component: RegistrationShippingAddressComponent,
        resolve: { registrationStep: RegistrationStepResolver },
        data: { registrationStep: StepType.ShippingAddress },
      },
      {
        path: 'terms',
        component: RegistrationTermsAndConditionsComponent,
        resolve: { registrationStep: RegistrationStepResolver },
        data: { registrationStep: StepType.TermsAndConditions },
      },
      {
        path: 'authentication',
        component: RegistrationProviderAuthenticationComponent,
        resolve: { registrationStep: RegistrationStepResolver },
        data: { registrationStep: StepType.ProviderAuthentication2 },
      },
      {
        path: 'accounts',
        component: RegistrationProviderTranslationComponent,
        resolve: { registrationStep: RegistrationStepResolver },
        data: { registrationStep: StepType.ProviderTranslation },
      },
      {
        path: 'operatorname',
        component: RegistrationOperatorNameComponent,
        resolve: { registrationStep: RegistrationStepResolver },
        data: { registrationStep: StepType.OperatorName },
      },
      {
        path: 'childquestions',
        component: RegistrationChildQuestionsComponent,
        resolve: { registrationStep: RegistrationStepResolver },
        data: { registrationStep: StepType.ChildQuestions },
      },
      {
        path: 'questions',
        component: RegistrationQuestionsComponent,
        resolve: { registrationStep: RegistrationStepResolver },
        data: { registrationStep: StepType.Questions },
      },
      {
        path: 'partnerinvite',
        component: RegistrationPartnerInviteComponent,
        resolve: { registrationStep: RegistrationStepResolver },
        data: { registrationStep: StepType.ProcessPartnerRequest },
      },
      {
        path: 'optoutalerts',
        component: RegistrationOptOutAlertsComponent,
        resolve: { registrationStep: RegistrationStepResolver },
        data: { registrationStep: StepType.OptOutAlerts },
      },
      {
        path: 'optoutemails',
        component: RegistrationOptOutEmailsComponent,
        resolve: { registrationStep: RegistrationStepResolver },
        data: { registrationStep: StepType.OptOutEmails },
      },
      {
        path: 'provideroauth',
        component: RegistrationProviderOauthComponent,
        resolve: { registrationStep: RegistrationStepResolver },
        data: { registrationStep: StepType.ProviderOAuth },
      },
      {
        path: 'payment',
        children: [
          {
            resolve: { member: MemberResolver, address: AddressResolver, registrationStep: RegistrationStepResolver },
            path: 'result',
            component: RegistrationPaymentCallbackPageComponent,
          },
        ],
      },
    ],
  },

  { path: 'sso', component: SingleSignOnComponent },
  { path: 'login/singleSignOn/:providerId', component: SingleSignOnComponent },
  { path: 'login/ss/login.aspx', component: V1SingleSignOnComponent },
  { path: 'maintenance', component: MaintenancePageComponent },
  { path: '**', redirectTo: '404page' },
];

@NgModule({
  imports: [RouterModule.forRoot(routes, { scrollPositionRestoration: 'enabled', relativeLinkResolution: 'legacy' })],
  exports: [RouterModule],
})
export class AppRoutingModule {}
